import { Illustration, Pill, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, FullScreenDialog } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useIsJobsEnabled } from "@src/appV2/redesign/PlacementCandidate/useIsJobsEnabled";

import { Button } from "../../../components/Button";

interface RestrictedBottomSheetProps {
  readonly modalState: UseModalState;
  readonly isFinalRestriction: boolean;
  readonly liftTime?: string;
  readonly reEnrollmentScore: number;
  readonly onContinue: () => void;
}

export function RestrictedBottomSheet(props: RestrictedBottomSheetProps) {
  const { modalState, isFinalRestriction, liftTime, reEnrollmentScore, onContinue } = props;
  const isJobsEnabled = useIsJobsEnabled();

  useLogEffect(
    APP_V2_USER_EVENTS.CLIPBOARD_SCORE_RESTRICTED_BOTTOM_SHEET_VIEWED,
    {},
    {
      enabled: modalState.modalIsOpen,
    }
  );

  const handleContinueClick = (): void => {
    logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_RESTRICTED_BOTTOM_SHEET_CONTINUE_CLICKED, {});
    onContinue();
  };

  function getDescription() {
    if (isFinalRestriction) {
      return (
        <Stack spacing={5}>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            You&apos;ve been removed from your shifts, and you can&apos;t book open shifts at this
            time. When your Restriction lifts, your score will reset to {reEnrollmentScore}.
          </Text>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            Since this is the 3rd time in the past 6 months, this Restriction lasts for 1 year.
          </Text>
        </Stack>
      );
    }

    return (
      <Stack spacing={5}>
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          As a result of your Clipboard Score reaching 0, you&apos;ve been removed from your future
          shifts, and you can&apos;t book new shifts at this time. When your Restriction lifts, your
          score will reset to {reEnrollmentScore}.
        </Text>
      </Stack>
    );
  }

  const description = getDescription();

  return (
    <FullScreenDialog
      modalState={modalState}
      variant="tertiary"
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(247, 208, 208, 1) 100%)",
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={10}
          sx={{
            paddingTop: 10,
            padding: 8,
            textAlign: "center",
            minHeight: "calc(100vh - 250px)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Illustration type="circled-cross-with-exclamation" />

          <Text variant="h3" textAlign="center">
            Your account is restricted
          </Text>

          {description}

          {isJobsEnabled && (
            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
              This restriction does not impact your access to Jobs on Clipboard: you can still apply
              and interview for permanent positions with workplaces.
            </Text>
          )}

          {liftTime && (
            <Pill
              iconType="clock-filled"
              label={`Restriction lifts on ${liftTime}`}
              size="small"
              variant="outlined"
              sx={{
                display: "inline-flex",
                backgroundColor: `rgba(255, 255, 255, 0.7)`,
                padding: 5,
                gap: 3,
              }}
            />
          )}
        </Stack>
      </Box>
      <Stack
        sx={{
          flex: 1,
          paddingX: 5,
          paddingBottom: 8,
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          bold
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={() => {
            void openZendeskMessaging();
            onContinue();
          }}
        >
          Tap if you think this is a mistake, or if you&apos;d like to speak with a representative.
        </Text>
      </Stack>
      <DialogFooter
        orientation="horizontal"
        sx={{
          background: "transparent",
        }}
      >
        <Button fullWidth variant="contained" size="large" onClick={handleContinueClick}>
          Got it
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
